/* eslint-disable stylistic/quote-props */
/**
 * King Living Checkbox Theme
 *
 * Global checkbox styling system supporting:
 * - 4 states: default, checked, default disabled, checked disabled
 * - Square with slightly rounded corners
 * - Light grey border for active states
 * - Darker grey border for disabled states
 * - Black background when checked
 * - Medium grey background when checked and disabled
 * - Custom checkmark icon
 *
 * @override Extends @salesforce/retail-react-app checkbox theme
 */

import baseCheckbox from '@salesforce/retail-react-app/app/theme/components/base/checkbox';

export default {
  ...baseCheckbox,

  baseStyle: {
    ...baseCheckbox.baseStyle,
    control: {
      ...baseCheckbox.baseStyle.control,
      // Default state: white background, dark border (matches Figma)
      w: 6, // 24px
      h: 6, // 24px
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'brandColors.greyscale30', // Normal state border
      backgroundColor: 'white',
      borderRadius: '4px',
      transition: 'all 0.2s ease',
      padding: 0, // No spacing between border and content
      display: 'flex', // Flexbox for centering
      alignItems: 'center', // Center vertically
      justifyContent: 'center', // Center horizontally
      position: 'relative', // For positioning

      // Checked state: full black square with white checkmark
      _checked: {
        'backgroundColor': 'brandColors.greyscale',
        'borderColor': 'brandColors.greyscale',
        'borderWidth': '1.5px',
        'color': 'white',
        '_hover': {
          borderColor: 'brandColors.greyscale',
        },
        // Checked disabled state: transparent container, child span with medium grey background
        '_disabled': {
          'backgroundColor': 'brandColors.greyscale50',
          'borderColor': 'brandColors.greyscale50',
          'color': 'white',
          'cursor': 'not-allowed',
          'opacity': 1,
        },
      },

      // Default disabled state: light grey tint background + medium grey border (Figma: greyscale/10 keyline)
      _disabled: {
        backgroundColor: 'brandColors.greyscale10',
        borderColor: 'brandColors.greyscale30',
        borderWidth: '1px',
        cursor: 'not-allowed',
        opacity: 1,
      },
    },
    // Icon wrapper div element - child div with black background
    icon: {
      ...(baseCheckbox.baseStyle?.icon || {}),
      backgroundColor: 'transparent',
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '4px',
      _checked: {
        width: '100%',
        height: '100%',
        backgroundColor: 'transparent',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // Checked disabled state: medium grey background
        _disabled: {
          width: '100%',
          backgroundColor: 'transparent',
        },
      },
    },
    label: {
      ...baseCheckbox.baseStyle.label,
      marginStart: 3,
    },
    container: {
      ...baseCheckbox.baseStyle.container,
    },
  },

  sizes: {
    ...baseCheckbox.sizes,
    md: {
      ...baseCheckbox.sizes.md,
      control: {
        w: 6, // 24px
        h: 6, // 24px
      },
    },
  },

  variants: {
    ...baseCheckbox.variants,
    checkout: {
      control: {
        borderColor: 'brandColors.greyscale',
        borderWidth: '1.5px',
        marginTop: {base: 0.5, lg: 0},
      },
      label: {
        marginLeft: 0,
        marginStart: 0,
        color: 'greyscale.100',
        fontFamily: 'body',
        fontWeight: 400,
        lineHeight: 1.4,
        fontSize: {base: 'sm', md: 'md'},
      },
      container: {
        alignItems: 'center',
        gap: '12px',
      },
    },
    preferred: {
      control: {
        w: 6,
        h: 6,
        borderColor: 'brandColors.greyscale',
        borderWidth: '1.5px',
        marginTop: {base: 0.5, lg: 0},
      },
      label: {
        marginLeft: 3,
        color: 'brandColors.greyscale',
        fontFamily: 'body',
        fontWeight: 400,
        lineHeight: 'base',
        fontSize: {base: 'sm', md: 'md'},
      },
      container: {
        alignItems: {base: 'flex-start', lg: 'center'},
      },
    },
  },

  defaultProps: {
    ...baseCheckbox.defaultProps,
    variant: 'default',
  },
};
